import { Legend, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from 'recharts';

export type KmiSpiderItem = {
  pillar: string;
  /** null = pilier N/A (0 exclu du calcul) */
  score: number | null;
};

export type KmiSpiderVariant = 'actuelle' | 'cible';

const VARIANT_STYLES: Record<
  KmiSpiderVariant,
  { stroke: string; fill: string; scoreBg: string; scoreBorder: string; scoreText: string }
> = {
  actuelle: {
    stroke: '#10B981',
    fill: '#10B981',
    scoreBg: 'bg-emerald-50',
    scoreBorder: 'border-emerald-200',
    scoreText: 'text-emerald-700',
  },
  cible: {
    stroke: '#6366F1',
    fill: '#6366F1',
    scoreBg: 'bg-indigo-50',
    scoreBorder: 'border-indigo-200',
    scoreText: 'text-indigo-700',
  },
};

export default function KmiSpiderChart({
  className = '',
  items,
  title = 'Spider KMI — moyenne par pilier',
  variant = 'actuelle',
}: {
  className?: string;
  items: KmiSpiderItem[];
  title?: string;
  variant?: KmiSpiderVariant;
}) {
  const styles = VARIANT_STYLES[variant];
  const scoredItems = items.filter((i) => i.score != null && Number.isFinite(Number(i.score)));
  const avg = scoredItems.length
    ? Math.round((scoredItems.reduce((s, i) => s + (Number(i.score) || 0), 0) / scoredItems.length) * 10) / 10
    : 0;

  const data = items.map((i) => ({
    pillar: i.pillar,
    Score:
      i.score != null && Number.isFinite(Number(i.score))
        ? Math.max(0, Math.min(5, Number(i.score)))
        : null,
  }));

  return (
    <div className={`bg-white rounded-lg p-6 shadow-md ${className}`}>
      <div className="mb-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-bz-dark mb-1">📊 {title}</h3>
            <p className="text-gray-600 text-sm">Échelle 1 → 5 par pilier (0 = N/A, exclu du calcul).</p>
          </div>
          <div className={`border rounded-lg p-3 ${styles.scoreBg} ${styles.scoreBorder}`}>
            <div className={`text-sm font-medium ${styles.scoreText}`}>Score global</div>
            <div className={`text-2xl font-bold ${styles.scoreText}`}>{avg} / 5</div>
          </div>
        </div>
      </div>

      <div className="h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} margin={{ top: 20, right: 70, bottom: 20, left: 70 }}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 12, fill: '#374151' }} />
            <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10, fill: '#6b7280' }} tickCount={6} />
            <Radar
              name={variant === 'cible' ? 'Maturité cible Futur' : 'Maturité actuelle'}
              dataKey="Score"
              stroke={styles.stroke}
              fill={styles.fill}
              fillOpacity={variant === 'cible' ? 0.1 : 0.12}
              strokeWidth={variant === 'cible' ? 2 : 3}
              strokeDasharray={variant === 'cible' ? '6 4' : undefined}
              dot={{ fill: styles.stroke, strokeWidth: 2, r: variant === 'cible' ? 3 : 4 }}
              connectNulls={false}
            />
            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
